import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useTheme, alpha } from "@mui/material/styles";

const ROLE_COLORS = [
  { name: "Orange", value: "#FFA726" },
  { name: "Lime", value: "#66BB6A" },
  { name: "Red", value: "#EF5350" },
  { name: "Purple", value: "#8E24AA" },
  { name: "Blue", value: "#29B6F6" },
  { name: "Green", value: "#43A047" },
  { name: "Pink", value: "#EC407A" },
  { name: "Cyan", value: "#26C6DA" },
  { name: "Indigo", value: "#5C6BC0" },
  { name: "Magenta", value: "#C2185B" },
];

export default function RolesManager({
  selectedDepartmentId,
  selectedLocationId,
  onDeleteRole,
  embedded = false,
  departmentName = "",
}) {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [roleColor, setRoleColor] = useState(ROLE_COLORS[0].value);
  const [editingRole, setEditingRole] = useState(null);
  const [editingOpen, setEditingOpen] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [editingColor, setEditingColor] = useState(ROLE_COLORS[0].value);

  useEffect(() => {
    if (!selectedDepartmentId) return setRoles([]);
    let unsub = null;
    (async () => {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return setRoles([]);
      const q = query(
        collection(db, `tenants/${tenantId}/roles`),
        where("departmentId", "==", selectedDepartmentId),
        orderBy("name")
      );
      unsub = onSnapshot(q, snap =>
        setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => !r.archived))
      );
    })();
    return () => { if (unsub) unsub(); };
  }, [selectedDepartmentId]);

  const handleAdd = async () => {
    if (!newRole.trim() || !selectedDepartmentId) return;
  const tenantId = await tryResolveTenantId();
  if (!tenantId) return;
  await addDoc(collection(db, `tenants/${tenantId}/roles`), {
      name: newRole.trim(),
      departmentId: selectedDepartmentId,
  locationId: selectedLocationId || null,
      color: roleColor,
    });
    setNewRole("");
    setRoleColor(ROLE_COLORS[0].value);
  };

  const handleDelete = async (id) => {
  const tenantId = await tryResolveTenantId();
  if (!tenantId) return;
  // Archive role so historical shifts retain roleName
  await updateDoc(doc(db, `tenants/${tenantId}/roles`, id), { archived: true, updatedAt: new Date() });
  };

  if (!selectedDepartmentId) return (
    <Box
      component={embedded ? Box : Paper}
      sx={{ p: embedded ? 0 : 3, bgcolor: embedded ? 'transparent' : theme.palette.background.paper }}
    >
      <Typography variant="h6" color="primary.main">Roles</Typography>
      <Typography color="text.secondary" sx={{ mt: 2 }}>Select a department to manage roles.</Typography>
    </Box>
  );

  return (
    <Box
      component={embedded ? Box : Paper}
      sx={{
        p: embedded ? 0 : { xs: 2, sm: 4 },
        mb: embedded ? 0 : 4,
        borderRadius: embedded ? 0 : 4,
        boxShadow: embedded ? 'none' : 3,
        bgcolor: embedded ? 'transparent' : theme.palette.background.paper
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant={embedded ? "h6" : "h5"} fontWeight={700} gutterBottom color="primary.main">
          Roles
        </Typography>
        {embedded && (
          <Typography variant="body2" color="text.secondary">
            Roles inside {departmentName || "the selected department"}.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "flex-end", flexWrap: 'wrap' }}>
        <TextField
          label="New Role"
          value={newRole}
          onChange={e => setNewRole(e.target.value)}
          fullWidth
          size="small"
          sx={{ maxWidth: 300, input: { color: theme.palette.text.primary } }}
        />
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Color</InputLabel>
          <Select
            value={roleColor}
            label="Color"
            onChange={e => setRoleColor(e.target.value)}
            renderValue={selected => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 18, height: 18, borderRadius: "50%",
                  bgcolor: selected,
                  border: "1.5px solid #bbb", mr: 1,
                }} />
                <span>
                  {ROLE_COLORS.find(opt => opt.value === selected)?.name || "Color"}
                </span>
              </Box>
            )}
          >
            {ROLE_COLORS.map(c => (
              <MenuItem key={c.value} value={c.value}>
                <Box sx={{
                  width: 18, height: 18, borderRadius: "50%",
                  bgcolor: c.value, display: "inline-block", mr: 1,
                  border: "1.5px solid #bbb",
                }} />
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleAdd} sx={{ height: 40 }}>
          Add
        </Button>
      </Box>
      <Table
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: 'hidden',
          background: embedded ? 'transparent' : alpha(theme.palette.background.paper, 0.9)
        }}
      >
        <TableHead>
          <TableRow sx={{ background: alpha(theme.palette.background.paper, 0.8) }}>
            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Color</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map(r => (
            <TableRow key={r.id} hover sx={{ transition: 'background 0.2s', cursor: 'pointer' }}>
              <TableCell>
                <Box
                  sx={{
                    display: "inline-block",
                    px: 2,
                    py: 0.5,
                    borderRadius: "20px",
                    fontWeight: 600,
                    color: "#fff",
                    bgcolor: r.color || "#888",
                    fontSize: "1rem",
                    boxShadow: 1,
                  }}
                >
                  {r.name}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{
                  width: 28, height: 18, borderRadius: 10, bgcolor: r.color || "#888",
                  border: "1.5px solid #ccc", display: "inline-block"
                }} />
              </TableCell>
              <TableCell align="right">
                <IconButton color="primary" onClick={() => { setEditingRole(r); setEditingName(r.name || ''); setEditingColor(r.color || ROLE_COLORS[0].value); setEditingOpen(true); }}>
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => { if (typeof onDeleteRole === 'function') { onDeleteRole(r); return; } handleDelete(r.id); }}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Role Dialog */}
      <Dialog open={editingOpen} onClose={() => setEditingOpen(false)}>
        <DialogTitle>Edit role</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            fullWidth
            margin="dense"
          />
          <FormControl sx={{ minWidth: 160, mt: 2 }} size="small">
            <InputLabel>Color</InputLabel>
            <Select
              value={editingColor}
              label="Color"
              onChange={e => setEditingColor(e.target.value)}
              renderValue={selected => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: "50%",
                    bgcolor: selected,
                    border: "1.5px solid #bbb", mr: 1,
                  }} />
                  <span>
                    {ROLE_COLORS.find(opt => opt.value === selected)?.name || "Color"}
                  </span>
                </Box>
              )}
            >
              {ROLE_COLORS.map(c => (
                <MenuItem key={c.value} value={c.value}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: "50%",
                    bgcolor: c.value, display: "inline-block", mr: 1,
                    border: "1.5px solid #bbb",
                  }} />
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            if (!editingRole || !editingName.trim()) return;
            const tenantId = await tryResolveTenantId();
            if (!tenantId) return;
            await updateDoc(doc(db, `tenants/${tenantId}/roles`, editingRole.id), { name: editingName.trim(), color: editingColor });
            setEditingOpen(false);
            setEditingRole(null);
            setEditingName('');
            setEditingColor(ROLE_COLORS[0].value);
          }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
